import numeral from 'numeral';
import moment from 'moment';
import Alert from 'react-s-alert';
import { addLocaleData } from 'react-intl';
import LogRocket from 'logrocket';
import TagManager from 'react-gtm-module';
import { DEFAULT_SUBMODALITY_ID, MODALITIES_CONVENCIONAL_662, SUBMODALITY_ID_CIRCULAR_662 } from '@constants/modalities';
import StringsHelper from '../strings/StringsHelper';
import { COOKIE_NAME } from '../modules/core/constants/values';

let classCounter = 0;

export function numbersOnly(value) {
  return value.replace(/[^0-9]/g, '');
}

export function validateEmailFormat(value) {
  // [^\s@à-ú(\\)\\"[\]@<>,;:] = Accept all characters different than space,
  // @, vogals from á to ù independent of case, (), [], ",", ";", ":", <>, \, ´ and "
  const reg = new RegExp(
    /^[^\s@à-ú(\\)\\"[\]@<>,;:´]+@[^\s@à-ú(\\)\\"[\]@<>,;:´]+\.[^\s@à-ú(\\)\\"[\]@<>,;:´]+$/gi
  );
  return reg.test(value);
}

export function getParameterByName(_name, _url) {
  let name = _name;
  let url = _url;

  if (!url) url = window.location.href;

  name = name.replace(/[[\]]/g, '\\$&');
  const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);

  const results = regex.exec(url);

  if (!results) return null;
  if (!results[2]) return '';
  if (!results[2] === 'null') return null;

  return results[2];
}

export function customDecodeURIComponent(uri) {
  try {
    let decoded = decodeURIComponent(uri);
    if (decoded === 'null') decoded = null;
    return decoded;
  } catch {
    return null;
  }
}

export function generateClassName(rule, styleSheet) {
  classCounter += 1;

  const escapeRegex = /([[\].#*$><+~=|^:(),"'`\s])/g;

  if (process.env.NODE_ENV === 'production') {
    return `jss${classCounter}`;
  }

  if (styleSheet && styleSheet.options.classNamePrefix) {
    let prefix = styleSheet.options.classNamePrefix;
    // Sanitize the string as will be used to prefix the generated class name.
    prefix = prefix.replace(escapeRegex, '-');

    if (prefix.match(/^Mui/)) {
      return `${prefix}-${rule.key}`;
    }

    return `${prefix}-${rule.key}-${classCounter}`;
  }

  return `${rule.key}-${classCounter}`;
}

export function formatCnpj(_v = '') {
  let v = _v;
  v = v.replace(/\D/g, ''); // Remove tudo o que não é dígito
  v = v.replace(/^(\d{2})(\d)/, '$1.$2'); // Coloca ponto entre o segundo e o terceiro dígitos
  v = v.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3'); // Coloca ponto entre o quinto e o sexto dígitos
  v = v.replace(/\.(\d{3})(\d)/, '.$1/$2'); // Coloca uma barra entre o oitavo e o nono dígitos
  v = v.replace(/(\d{4})(\d)/, '$1-$2'); // Coloca um hífen depois do bloco de quatro dígitos
  return v;
}

export function validatePassword(value) {
  const reg = new RegExp('^(?=.*[a-z])(?=.*[0-9])(?=.{8,})');
  return reg.test(value);
}

export function validatePhone(value) {
  return value.replace(/[^0-9]/g, '').length >= 10;
}

export function installmentsAdapter(installments) {
  const options = installments.map((i) => {
    const { parcelas } = i;
    const n = parcelas.length;
    const total = parcelas[0].premio + parcelas[0].af;

    return {
      numeroVezes: n,
      valor: total,
    };
  });

  return options;
}

export function formatValue(value, type) {
  let years = 0;
  let label = '';

  switch (type) {
    case 'currency':
      return numeral(value).format();
    case 'date':
      return moment(value).format('DD/MM/YYYY');
    case 'days':
      return `${value} dias`;
    case 'years':
      years = parseInt(value / 365, 10);
      label = years > 1 ? 'anos' : 'ano';
      return `${years} ${label}`;
    case 'cnpj':
      return formatCnpj(value);
    case 'processNumber':
      return value.replace(/^(\d{7})(\d{2})(\d{4})(\d{3})(\d{4})/, '$1-$2.$3.$4.$5');
    case 'timeMinutes':
      return moment(value).format('HH[h]mm');
    default:
      return value;
  }
}

export function registerLocales() {
  const modalities = require('../modules/core/json/modalities.json');

  modalities.forEach((m) => {
    const { type } = m;
    addLocaleData({ locale: type, parentLocale: 'en' });
  });
}

export function getStringsByModalityId(modalityId) {
  const modalityType = getModalityTypeById(modalityId);
  return StringsHelper.getMessagesByModality(modalityType);
}

export function getModalityTypeById(modalityId) {
  const modalities = require('../modules/core/json/modalities.json');
  const modalityType = modalities.find((m) => m.id === modalityId).type;

  return modalityType;
}

export function getPremioTotalLabel(total, installments = 1) {
  let label = numeral(total).format();

  if (installments > 1) {
    label += ` em ${installments}x`;
  }

  return label;
}

export function displayErrorAlert(message, config = {}) {
  Alert.error(message, {
    position: 'top-right',
    effect: 'bouncyflip',
    ...config,
  });
}

export function getTokenQueryStringValue(tokenName) {
  // Do not decode the token
  return getParameterByName(tokenName, window.location.href);
}

export function validateTokenQueryString(tokenName) {
  const token = getTokenQueryStringValue(tokenName);

  if (!token) {
    return false;
  }

  return true;
}

export function isErrorCodeAnalysis(errorCode) {
  const codes = require('../json/quoteAnalysisErrorCodes.json');
  return codes.includes(errorCode);
}

export function getLeadData() {
  const data = localStorage?.getItem('leadData');

  if (!data) return null;

  const dataObj = JSON.parse(data);

  if (!dataObj?.token) return null;

  const { dataCriacao } = dataObj;
  const now = moment().format('x');
  const expireDate = moment(dataCriacao).add(30, 'days').format('x');

  if (now > expireDate) {
    localStorage.removeItem('leadData');
    return null;
  }

  return dataObj;
}

export function shouldSaveLeadData() {
  const shouldSkip = sessionStorage?.getItem('skipLeadData') || false;
  return !shouldSkip;
}

export function deleteLeadData() {
  if (localStorage) {
    localStorage.removeItem('leadData');
  }
}

export function truncateString(str, length) {
  const strLength = str.length;

  if (strLength <= length) {
    return str;
  }

  return `${str.substring(0, length)}...`;
}

export function convertObjectToQueryParameter(obj) {
  const keys = Object.keys(obj);
  let query = '';

  if (obj.length === 0) {
    return '';
  }

  keys.forEach((k) => {
    query += `${k}=${encodeURIComponent(obj[k])}&`;
  });

  return query.substring(0, query.length - 1); // Remove last ampersand
}

export function getCampaignInfoFromURL() {
  let campaignSource = getParameterByName('utm_source') || '';
  const campaign = getParameterByName('utm_campaign') || 'Orgânico';
  const medium = getParameterByName('utm_medium');
  const term = getParameterByName('utm_term') || '';
  const content = getParameterByName('utm_content') || '';
  const gclid = getParameterByName('gclid') || '';

  if (medium) {
    campaignSource = `${campaignSource}/${medium}`;
  }

  return {
    origemCampanha: campaignSource,
    campanha: campaign,
    utmTerm: term,
    utmContent: content,
    gclid,
  };
}

export function getCampaignInfo() {
  const existingCookie = getCookieByName(COOKIE_NAME);
  if (!existingCookie) return null;

  try {
    return JSON.parse(atob(existingCookie));
  } catch (e) {
    LogRocket.captureMessage('Error JS atob');
    return null;
  }
}

export function saveCampaignCookie() {
  const data = getCampaignInfoFromURL();

  if (!data.origemCampanha) return;

  try {
    const domain = process.env.REACT_APP_COOKIE_DOMAIN;
    const cookieValue = btoa(JSON.stringify(data));
    const dt = new Date();
    dt.setTime(dt.getTime() + 1 * 24 * 60 * 60 * 1000);
    const expires = dt.toGMTString();
    document.cookie = `${COOKIE_NAME}=${cookieValue}; expires=${expires}; path=/; domain=${domain}`;
  } catch (e) {
    LogRocket.captureMessage('Error JS btoa');
  }
}

/**
 * Source: https://www.w3schools.com/js/js_cookies.asp
 */
export function getCookieByName(cname) {
  const name = `${cname}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return null;
}

export function isBrowserReturning(history) {
  if (history.action === 'POP') {
    return true;
  }

  return false;
}

export function capitalizeString(string) {
  if (!string) return '';

  const splittedString = string.split(' ');
  const splitWords = splittedString.map((s) => {
    return s.split('').map((letter, index) => {
      if (index !== 0 && s[index + 1] !== '.') return letter.toLowerCase();

      return letter;
    });
  });

  const capitalizedWords = splitWords.map((words) => words.join(''));
  return capitalizedWords.join(' ');
}

export function diffPercentage(valueA, valueB) {
  const diff = Math.abs(valueA - valueB);
  const avg = (valueA + valueB) / 2;

  return (diff / avg) * 100;
}

export function fireGTMEvent(event, config = {}) {
  TagManager.dataLayer({
    dataLayer: {
      event,
      ...config,
    },
  });
}

export function fireJuntoEventManager(eventCategory, eventLabel, eventAction, eventValue) {
  let args = {
    event: 'event-trigger',
    eventCategory,
  };
  if (eventLabel) {
    args = { ...args, eventLabel };
  }
  if (eventAction) {
    args = { ...args, eventAction };
  }
  if (eventValue) {
    args = { ...args, eventValue };
  }
  TagManager.dataLayer({
    dataLayer: args,
  });
}

export function forceDownload(fileUrl, fileName = '', target = '_self') {
  const link = document.createElement('a');
  link.href = fileUrl;
  link.target = target;
  link.download = fileName || fileUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// TODO: BEX-1706 Remover codigo inativo medida salvamento
export function getDefaultSubmodalityIdByModalityId(isCircular662Active, ModalityId) {
  return isCircular662Active && MODALITIES_CONVENCIONAL_662.includes(ModalityId) ?
    SUBMODALITY_ID_CIRCULAR_662 :
    DEFAULT_SUBMODALITY_ID;
}

export function checkSubmodalityIsConvencional(submodalidadeId) {
  return [DEFAULT_SUBMODALITY_ID, SUBMODALITY_ID_CIRCULAR_662].includes(submodalidadeId);
}
